import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import Stack from '@mui/joy/Stack';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';


import { SidebarLink } from './Sidebar';
import SidebarList from './SidebarList';
import IconLink from 'components/IconLink';
import AssistantOnly from 'components/AssistantOnly';

import { getIcon } from 'utils/misc';
import useCollabInvite from 'hooks/useCollabInvite';
import useUserIdentity from 'hooks/useUserIdentity';
import useCollaborators from 'hooks/useCollaborators';
import useFHRequest from 'hooks/useFHRequest';
import useAuth from 'hooks/useAuth';

// type tt_options = 'bottom-end' | 'bottom-start' | 'bottom' | 'left-end' | 'left-start' | 'left' | 'right-end' | 'right-start' | 'right' | 'top-leend' | 'top-start' | 'top';

const AssistantCollabs = observer(({tooltipPlacement}: {tooltipPlacement: 'bottom-end' | 'right'}) => {
    const location = useLocation().pathname;
    const {
        fetchListAvailable,
        fetchListClaimed,
        getListAvailable,
        getListClaimed,
        //        getListClosed
    } = useFHRequest();

    const { currentIdentity } = useUserIdentity();

    const { currentUser } = useAuth();
    const { getAvailableInvites, getMutualInvites } = useCollabInvite();

    const [fetched, setFetched] = useState(false);
    const [claimedRequests, setClaimedRequests] = useState(0);
    const {
        activeCollaborators,
        //        snoozedCollaborators,
        //        archivedCollaborators,
    } = useCollaborators();

    // const invites = store.getInvitesByStatus('available');
    const mutualInvites = getMutualInvites(currentIdentity?.key).length;
    const availableInvites = getAvailableInvites().length;
    const availableRequests = getListAvailable().length;
    const collaborators = activeCollaborators.length;

    //    const closedRequests = getListClosed(currentIdentity.key, currentUser.key).length;

    const AcceptedIcon = getIcon('AssignmentInd');
    const InvitesIcon = getIcon('MoveToInbox');
    const AvailableIcon = getIcon('Inbox');
    const SnoozedIcon = getIcon('PendingActions');
    const ArchivedIcon = getIcon('AllInbox');

    useEffect(() => {
        if(!currentIdentity?.uuid) return;
        if (!fetched) {
            fetchListAvailable(currentIdentity.uuid);
            setFetched(true);
        }
    }, [fetched, fetchListAvailable, fetchListClaimed, currentIdentity?.uuid]);


    useEffect(() => {
        // console.log('claimed requests', getListClaimed(currentIdentity.key, currentUser.key).length);
        setClaimedRequests(getListClaimed(currentIdentity.key, currentUser.key).length);
    }, [getListClaimed]);

    return (


        <SidebarList>

            <AssistantOnly allowIC={true}>
                <SidebarLink to={"/collabs"}>
                    <ListItem
                        color={location == '/collabs' ? "primary" : 'neutral'}
                        variant={location == '/collabs' ? "soft" : 'plain'}
                    >
                        <Tooltip arrow
                            placement={tooltipPlacement}
                            title="The collabs you're working on.">
                            <ListItemButton  >
                                <ListItemDecorator>
                                    <AcceptedIcon />
                                </ListItemDecorator>
                                <ListItemContent>Yours</ListItemContent>
                                <Chip>{collaborators + claimedRequests}</Chip>
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                </SidebarLink>
            </AssistantOnly>



            { mutualInvites > 0 &&
                <SidebarLink to={"/invites/mutual"}>
                    <Tooltip arrow
                        placement={tooltipPlacement}
                        title="You've been invited to these. You specifically.">
                        <ListItem
                            color={location == '/invites/mutual' ? "primary" : 'neutral' }
                            variant={location == '/invites/mutual' ? "soft" : 'plain' }
                        >
                            <ListItemButton>
                                <ListItemDecorator>
                                    <InvitesIcon />
                                </ListItemDecorator>

                                <ListItemContent>Invited</ListItemContent>
                                <Chip
                                    color={ mutualInvites  ? 'success' : 'neutral'}
                                >{ mutualInvites }</Chip>
                            </ListItemButton>
                        </ListItem>
                    </Tooltip>
                </SidebarLink>
            }








            <AssistantOnly allowIC={true}>
                <SidebarLink to={"/collabs/snoozed"}>
                    <ListItem
                        color={location == '/collabs/snoozed' ? "primary" : 'neutral'}
                        variant={location == '/collabs/snoozed' ? "soft" : 'plain'}
                    >
                        <Tooltip arrow
                            placement={tooltipPlacement}
                            title="If the client responds, we'll let you know.">
                            <ListItemButton  >
                                <ListItemDecorator>
                                    <SnoozedIcon />
                                </ListItemDecorator>
                                <ListItemContent>Snoozed</ListItemContent>
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                </SidebarLink>
                <SidebarLink to={"/collabs/archived"}>
                    <ListItem
                        color={location == '/collabs/archived' ? "primary" : 'neutral'}
                        variant={location == '/collabs/archived' ? "soft" : 'plain'}
                    >
                        <Tooltip arrow
                            placement={tooltipPlacement}
                            title="Closed requests and collabs that you've left.">
                            <ListItemButton  >
                                <ListItemDecorator>
                                    <ArchivedIcon />
                                </ListItemDecorator>
                                <ListItemContent>Archive</ListItemContent>
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                </SidebarLink>

                <Stack mt={2}>

            <AssistantOnly>
                <SidebarLink to={"/invites/available"}>
                    <Tooltip arrow
                        placement={tooltipPlacement}
                        title="These collabs are available.">
                        <ListItem
                            color={location == '/invites/available' ? "primary" : 'neutral'}
                            variant={location == '/invites/available' ? "soft" : 'plain'}
                        >
                            <ListItemButton >
                                <ListItemDecorator>
                                    <AvailableIcon />
                                </ListItemDecorator>

                                <ListItemContent>Available</ListItemContent>

                                <Chip>{availableInvites + availableRequests} </Chip>
                            </ListItemButton>
                        </ListItem>
                    </Tooltip>
                </SidebarLink>
            </AssistantOnly>

                <IconLink
                    role="support"
                    url="/open"
                    icon="DayOnesListOpen"
                    tooltip="View all of the open tasks, both claimed and unclaimed."
                    title="Open Tasks" />

                <IconLink
                    role="manager"
                    url="/mentor"
                    icon="DayOnesMentor"
                    tooltip="View the mentor queue."
                    title="Mentoring" />
                </Stack>
            </AssistantOnly>


        </SidebarList>


    );
});

export default function AssistantSidebar({tooltipPlacement = 'right'}: {tooltipPlacement: 'bottom-end' | 'right'}) {
    return (
        <>
            <AssistantCollabs tooltipPlacement={tooltipPlacement} />
        </>
    );
}
